import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Star, X } from "lucide-react";
import { http, fmtErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ReelUpload } from "../components/ReelUpload";
import { FulfillmentRow } from "../components/FulfillmentRow";
import { SubscriptionHealth } from "../components/SubscriptionHealth";
import { WaitlistToolbar } from "../components/LaunchBlast";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [apps, setApps] = useState([]);
  const [subs, setSubs] = useState([]);
  const [candyReqs, setCandyReqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [orders, setOrders] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [form, setForm] = useState({ title: "", url: "", author: "" });
  const [tform, setTform] = useState({ quote: "", author: "", source: "customer", rating: 5 });
  const [loading, setLoading] = useState(false);
  const [tloading, setTloading] = useState(false);

  useEffect(() => {
    if (user === false) nav("/login");
    if (user && user.role !== "admin") { toast.error("Admin only"); nav("/"); }
  }, [user, nav]);

  const refresh = () => {
    http.get("/reviews").then(({ data }) => setReviews(data.reviews));
    http.get("/wholesale/applications").then(({ data }) => setApps(data.applications)).catch(() => {});
    http.get("/subscriptions").then(({ data }) => setSubs(data.subscriptions)).catch(() => {});
    http.get("/candy-requests").then(({ data }) => setCandyReqs(data.requests)).catch(() => {});
    http.get("/testimonials").then(({ data }) => setTestimonials(data.testimonials)).catch(() => {});
    http.get("/admin/orders").then(({ data }) => setOrders(data.orders)).catch(() => {});
    http.get("/waitlist").then(({ data }) => setWaitlist(data.waitlist)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  const addReview = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/reviews", form);
      setForm({ title: "", url: "", author: "" });
      toast.success("Review added");
      refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await http.delete(`/reviews/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    setTloading(true);
    try {
      await http.post("/testimonials", tform);
      setTform({ quote: "", author: "", source: "customer", rating: 5 });
      toast.success("Testimonial added");
      refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setTloading(false); }
  };
  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await http.delete(`/testimonials/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const approveApp = async (id, business) => {
    if (!window.confirm(`Approve ${business}? They'll be unlocked for wholesale checkout if they have an account.`)) return;
    try {
      const { data } = await http.post(`/wholesale/applications/${id}/approve`);
      toast.success(data.user_updated ? "Approved · account unlocked" : "Approved · no matching account yet");
      refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const rejectApp = async (id, business) => {
    if (!window.confirm(`Reject ${business}? You can undo by approving later.`)) return;
    try {
      await http.post(`/wholesale/applications/${id}/reject`);
      toast.success("Rejected");
      refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const renewSub = async (s) => {
    if (!window.confirm(`Send ${s.name || s.email} a renewal charge link for $20?`)) return;
    try {
      const { data } = await http.post(`/subscriptions/${s.id}/renew`);
      toast.success("Renewal link sent · " + data.session_id.slice(0, 16) + "…");
      refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  if (!user || user.role !== "admin") return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]";

  return (
    <div className="bg-[#FFFDF8]" data-testid="admin-root">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Admin</div>
        <h1 className="font-heading text-6xl uppercase tracking-wide">Dashboard</h1>

        {/* Subscription Health KPI tiles */}
        <div className="mt-10">
          <SubscriptionHealth />
        </div>

        {/* Reviews */}
        <div className="mt-12">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Reviews & reels</h2>

          <ReelUpload onUploaded={refresh} />

          <div className="mt-6 mb-2 text-xs uppercase tracking-widest text-[#5C5A56]">Or paste an external link (TikTok / Instagram / YouTube)</div>
          <form onSubmit={addReview} className="bg-[#FDF4E3] border border-[#E5D8C1] p-6 grid md:grid-cols-3 gap-4">
            <label className="block"><span className="text-xs uppercase tracking-widest">Title *</span>
              <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className={input + " mt-2"} placeholder="e.g. Best chamoy ropes EVER" data-testid="admin-review-title" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Video URL *</span>
              <input required value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} className={input + " mt-2"} placeholder="TikTok / Instagram / YouTube link" data-testid="admin-review-url" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Author (optional)</span>
              <input value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} className={input + " mt-2"} placeholder="@handle" data-testid="admin-review-author" />
            </label>
            <div className="md:col-span-3">
              <button type="submit" disabled={loading} className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-8 py-3 uppercase tracking-widest font-heading disabled:opacity-60 flex items-center gap-2" data-testid="admin-review-add">
                {loading && <Loader2 className="animate-spin" size={16} />} Add review
              </button>
            </div>
          </form>

          <div className="mt-6 space-y-3">
            {reviews.length === 0 && <div className="text-[#5C5A56] italic">No reviews yet. Add TikTok/Instagram/YouTube reel URLs above and they will appear on the homepage.</div>}
            {reviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5D8C1] p-4 flex items-center gap-4" data-testid={`admin-review-row-${r.id}`}>
                <div className="text-xs uppercase tracking-widest text-[#5C5A56] w-24">{r.platform}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg uppercase leading-tight truncate">{r.title}</div>
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-[#5C5A56] hover:text-[#D32F2F] truncate block">{r.url}</a>
                </div>
                {r.author && <div className="text-xs text-[#5C5A56]">{r.author}</div>}
                <button onClick={() => deleteReview(r.id)} className="text-[#5C5A56] hover:text-[#D32F2F]" data-testid={`admin-review-delete-${r.id}`}><X size={18} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Text testimonials */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Customer testimonials ({testimonials.length})</h2>
          <form onSubmit={addTestimonial} className="bg-[#FDF4E3] border border-[#E5D8C1] p-6 grid md:grid-cols-3 gap-4" data-testid="admin-testimonial-form">
            <label className="md:col-span-3 block"><span className="text-xs uppercase tracking-widest">Quote *</span>
              <textarea required rows={2} value={tform.quote} onChange={(e) => setTform({...tform, quote: e.target.value})} className={input + " mt-2 resize-none"} placeholder="e.g. Best chamoy ropes I've ever tried, my kids are hooked!" data-testid="admin-testimonial-quote" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Author *</span>
              <input required value={tform.author} onChange={(e) => setTform({...tform, author: e.target.value})} className={input + " mt-2"} placeholder="e.g. Marisol V." data-testid="admin-testimonial-author" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Source</span>
              <select value={tform.source} onChange={(e) => setTform({...tform, source: e.target.value})} className={input + " mt-2"} data-testid="admin-testimonial-source">
                <option value="customer">Verified customer</option>
                <option value="tiktok">TikTok comment</option>
                <option value="instagram">Instagram DM</option>
                <option value="google">Google review</option>
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Rating (1-5)</span>
              <input type="number" min="1" max="5" value={tform.rating} onChange={(e) => setTform({...tform, rating: parseInt(e.target.value) || 5})} className={input + " mt-2"} data-testid="admin-testimonial-rating" />
            </label>
            <div className="md:col-span-3">
              <button type="submit" disabled={tloading} className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-8 py-3 uppercase tracking-widest font-heading disabled:opacity-60 flex items-center gap-2" data-testid="admin-testimonial-add">
                {tloading && <Loader2 className="animate-spin" size={16} />} Add testimonial
              </button>
            </div>
          </form>
          <div className="mt-6 space-y-3">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-white border border-[#E5D8C1] p-4 flex items-start gap-4" data-testid={`admin-testimonial-row-${t.id}`}>
                <div className="flex text-[#FFB300] pt-1">
                  {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1A1A1A] leading-snug">&ldquo;{t.quote}&rdquo;</div>
                  <div className="text-xs text-[#5C5A56] mt-1">— {t.author} · {t.source}{t.seed && <span className="ml-2 bg-[#FFB300] text-[#1A1A1A] px-1.5 py-0.5 text-[10px] tracking-widest uppercase">Sample</span>}</div>
                </div>
                <button onClick={() => deleteTestimonial(t.id)} className="text-[#5C5A56] hover:text-[#D32F2F]" data-testid={`admin-testimonial-delete-${t.id}`}><X size={18} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Applications */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Wholesale applications ({apps.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#1A1A1A] text-left text-xs uppercase tracking-widest">
                  <th className="py-3 pr-4">When</th><th className="pr-4">Business</th><th className="pr-4">Contact</th><th className="pr-4">Type</th><th className="pr-4">Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => {
                  const status = a.status || "pending";
                  const badge = {
                    pending: "bg-[#FFB300] text-[#1A1A1A]",
                    approved: "bg-[#7CB342] text-white",
                    rejected: "bg-[#5C5A56] text-white",
                  }[status] || "bg-[#FFB300] text-[#1A1A1A]";
                  return (
                    <tr key={a.id} className="border-b border-[#E5D8C1] align-top" data-testid={`admin-app-${a.id}`}>
                      <td className="py-3 pr-4 text-[#5C5A56] whitespace-nowrap">{a.created_at?.slice(0, 10)}</td>
                      <td className="pr-4 font-semibold">{a.business_name}<div className="text-[10px] uppercase tracking-widest text-[#5C5A56]">{a.expected_volume || "—"}{a.referred_by_code ? ` · ref ${a.referred_by_code}` : ""}</div></td>
                      <td className="pr-4">{a.contact_name}<br/><span className="text-xs text-[#5C5A56]">{a.email} · {a.phone}</span></td>
                      <td className="pr-4">{a.business_type}</td>
                      <td className="pr-4"><span className={`text-[10px] uppercase font-heading px-2 py-1 ${badge}`} data-testid={`admin-app-status-${a.id}`}>{status}</span></td>
                      <td className="whitespace-nowrap">
                        {status === "pending" ? (
                          <div className="flex gap-2">
                            <button onClick={() => approveApp(a.id, a.business_name)} className="bg-[#7CB342] hover:bg-[#689F38] text-white px-3 py-1.5 text-xs uppercase tracking-widest font-heading" data-testid={`admin-app-approve-${a.id}`}>Approve</button>
                            <button onClick={() => rejectApp(a.id, a.business_name)} className="border border-[#5C5A56] hover:bg-[#5C5A56] hover:text-white text-[#5C5A56] px-3 py-1.5 text-xs uppercase tracking-widest font-heading" data-testid={`admin-app-reject-${a.id}`}>Reject</button>
                          </div>
                        ) : status === "rejected" ? (
                          <button onClick={() => approveApp(a.id, a.business_name)} className="bg-[#7CB342] hover:bg-[#689F38] text-white px-3 py-1.5 text-xs uppercase tracking-widest font-heading" data-testid={`admin-app-approve-${a.id}`}>Approve now</button>
                        ) : (
                          <button onClick={() => rejectApp(a.id, a.business_name)} className="border border-[#5C5A56] hover:bg-[#5C5A56] hover:text-white text-[#5C5A56] px-3 py-1.5 text-xs uppercase tracking-widest font-heading" data-testid={`admin-app-reject-${a.id}`}>Undo</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Orders */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Orders ({orders.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#1A1A1A] text-left text-xs uppercase tracking-widest">
                  <th className="py-3 pr-4">When</th><th className="pr-4">Mode</th><th className="pr-4">Customer</th><th className="pr-4">Amount</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-[#E5D8C1] align-top" data-testid={`admin-order-${o.id}`}>
                    <td className="py-3 pr-4 text-[#5C5A56] whitespace-nowrap">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="pr-4"><span className="text-[10px] uppercase tracking-widest font-heading">{o.mode || "wholesale"}</span></td>
                    <td className="pr-4">{o.customer_name || o.customer_email || o.user_email || "—"}</td>
                    <td className="pr-4 font-heading">${(o.amount || 0).toFixed(2)}</td>
                    <td>
                      <div className="flex flex-col gap-1">
                        <span className={`text-[10px] uppercase font-heading px-2 py-1 w-fit ${o.payment_status === "paid" ? "bg-[#7CB342] text-white" : o.payment_status === "initiated" ? "bg-[#FFB300] text-[#1A1A1A]" : "bg-[#5C5A56] text-white"}`}>{o.payment_status}</span>
                        {o.fulfillment_status && (
                          <span className={`text-[10px] uppercase font-heading px-2 py-1 w-fit ${o.fulfillment_status === "delivered" ? "bg-[#1A1A1A] text-white" : o.fulfillment_status === "shipped" ? "bg-[#D32F2F] text-white" : "bg-[#E5D8C1] text-[#1A1A1A]"}`}>{o.fulfillment_status}</span>
                        )}
                        {o.payment_status === "paid" && (
                          <FulfillmentRow order={o} onUpdated={refresh} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-[#5C5A56] italic">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Chamoy Club subscribers */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Chamoy Club subscribers ({subs.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#1A1A1A] text-left text-xs uppercase tracking-widest">
                  <th className="py-3 pr-4">When</th><th className="pr-4">Name</th><th className="pr-4">Contact</th><th className="pr-4">Picks</th><th className="pr-4">Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-b border-[#E5D8C1] align-top" data-testid={`admin-sub-${s.id}`}>
                    <td className="py-3 pr-4 text-[#5C5A56] whitespace-nowrap">{s.created_at?.slice(0, 10)}</td>
                    <td className="pr-4 font-semibold">{s.name}</td>
                    <td className="pr-4">{s.email}<br/><span className="text-xs text-[#5C5A56]">{s.phone || ""}</span></td>
                    <td className="pr-4"><div className="text-xs">{(s.candy_choices || []).join(", ")}</div></td>
                    <td className="pr-4">
                      <span className={`text-[10px] uppercase font-heading px-2 py-1 ${s.status === "active" ? "bg-[#7CB342] text-white" : s.status === "pending_payment" ? "bg-[#FFB300] text-[#1A1A1A]" : "bg-[#5C5A56] text-white"}`}>{s.status}</span>
                      {s.last_renewal_link_sent_at && (
                        <div className="text-[10px] text-[#5C5A56] mt-1">Last renewal link: {s.last_renewal_link_sent_at.slice(0, 10)}</div>
                      )}
                    </td>
                    <td>
                      {s.status === "active" && (
                        <button onClick={() => renewSub(s)} className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-3 py-1.5 text-[10px] uppercase tracking-widest font-heading" data-testid={`admin-sub-renew-${s.id}`}>Send renewal $20</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Waitlist */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <h2 className="font-heading text-3xl uppercase tracking-wide">Waitlist ({waitlist.length})</h2>
            <WaitlistToolbar waitlistCount={waitlist.length} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#1A1A1A] text-left text-xs uppercase tracking-widest">
                  <th className="py-3 pr-4">When</th><th className="pr-4">Email</th><th className="pr-4">Name</th><th className="pr-4">Interest</th><th className="pr-4">Code</th><th className="pr-4">Refs</th><th className="pr-4">Referred by</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.map((w) => (
                  <tr key={w.id} className="border-b border-[#E5D8C1]" data-testid={`admin-waitlist-${w.id}`}>
                    <td className="py-3 pr-4 text-[#5C5A56] whitespace-nowrap">{w.created_at?.slice(0, 10)}</td>
                    <td className="pr-4 font-semibold">{w.email}</td>
                    <td className="pr-4">{w.name || "—"}</td>
                    <td className="pr-4"><span className="text-[10px] uppercase font-heading tracking-widest bg-[#FDF4E3] px-2 py-1">{w.interest}</span></td>
                    <td className="pr-4 font-mono text-xs">{w.referral_code || "—"}</td>
                    <td className="pr-4 font-heading text-[#7CB342]">{w.referrals_count ?? 0}</td>
                    <td className="pr-4 font-mono text-xs text-[#5C5A56]">{w.referred_by || "organic"}</td>
                    <td className="text-xs text-[#5C5A56]">{w.notes || "—"}</td>
                  </tr>
                ))}
                {waitlist.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-[#5C5A56] italic">Nobody on the waitlist yet. Once purchases are gated, signups will land here.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Candy requests (original) */}
        <div className="mt-16">
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-6">Candy suggestions ({candyReqs.length})</h2>
          <div className="space-y-3">
            {candyReqs.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5D8C1] p-4" data-testid={`admin-req-${r.id}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="font-heading text-lg uppercase leading-tight">{r.candy_name}</div>
                  <div className="text-xs text-[#5C5A56]">{r.created_at?.slice(0, 10)}</div>
                </div>
                <div className="text-sm text-[#5C5A56] mt-1">by {r.name} · {r.email}</div>
                {r.description && <div className="text-sm mt-2">{r.description}</div>}
                {r.why && <div className="text-sm text-[#5C5A56] mt-1 italic">Why: {r.why}</div>}
              </div>
            ))}
            {candyReqs.length === 0 && <div className="text-[#5C5A56] italic">No suggestions yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
